import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import ZoneHeatmapDisplay from './ZoneHeatmapDisplay';
import './QuestPerformance.css';

const CHART_COLORS = [
  '#2196F3', '#FF5722', '#4CAF50', '#9C27B0', '#FF9800',
  '#00BCD4', '#E91E63', '#8BC34A', '#3F51B5', '#FFEB3B',
];

function getPerformanceStatus(avgFps) {
  if (avgFps >= 45) return { className: 'good', label: 'Good', emoji: '\u2713' };
  if (avgFps >= 35) return { className: 'warning', label: 'Warning', emoji: '!' };
  return { className: 'critical', label: 'Critical', emoji: '\uD83D\uDED1' };
}

/**
 * Quest Performance tab.
 * Shows expandable quest sections with per-zone heatmap data and CL trends.
 */
function QuestPerformance({ api }) {
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedQuest, setExpandedQuest] = useState(null);
  const [days] = useState(30);

  // Load quest list
  useEffect(() => {
    let cancelled = false;
    async function fetchQuests() {
      setLoading(true);
      setError(null);
      try {
        const result = await api.getQuestPerformanceQuests(days);
        if (!cancelled) setQuests(result.quests || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchQuests();
    return () => { cancelled = true; };
  }, [api, days]);

  const toggleQuest = useCallback((questSetup) => {
    setExpandedQuest(prev => prev === questSetup ? null : questSetup);
  }, []);

  if (loading) {
    return <div className="quest-perf-loading"><div className="loading-spinner"></div> Loading quest performance data...</div>;
  }

  if (error) {
    return <div className="quest-perf-error">Error: {error}</div>;
  }

  if (quests.length === 0) {
    return (
      <div className="quest-perf-empty">
        <p>No quest performance data found.</p>
        <p className="quest-perf-hint">This tab shows data from AWS device quest runs (performance/quest/* tests). No AWS quest runs have been recorded in the last 30 days.</p>
      </div>
    );
  }

  return (
    <div className="quest-perf">
      <div className="quest-perf-header">
        <h2>Quest Performance</h2>
        <span className="quest-perf-count">{quests.length} quests</span>
      </div>

      <div className="quest-perf-list">
        {quests.map(quest => (
          <div key={quest.questSetup} className="quest-perf-item">
            <div className="quest-perf-item-header" onClick={() => toggleQuest(quest.questSetup)}>
              <span className="quest-expand-icon">{expandedQuest === quest.questSetup ? '\u25BC' : '\u25B6'}</span>
              <span className="quest-name">
                {quest.displayName}
                {quest.soId && <span className="quest-so-id">{quest.soId}</span>}
              </span>
              <span className="quest-stats">
                <span className="quest-pass-rate">{quest.passRate}% pass</span>
                <span className="quest-run-count">{quest.totalRuns} runs</span>
                <span className={`quest-result ${quest.passed > 0 ? 'pass' : 'fail'}`}>
                  {quest.passed}/{quest.totalRuns}
                </span>
              </span>
            </div>

            {expandedQuest === quest.questSetup && (
              <QuestDetail api={api} questSetup={quest.questSetup} days={days} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Expanded quest detail — shows zones and trend data.
 */
function QuestDetail({ api, questSetup, days }) {
  const [zones, setZones] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedZone, setExpandedZone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      try {
        const [zonesResult, trendResult] = await Promise.all([
          api.getQuestPerformanceZones(questSetup, days),
          api.getQuestPerformanceTrend(questSetup, days)
        ]);
        if (!cancelled) {
          const loadedZones = zonesResult.zones || [];
          setZones(loadedZones);
          setTrendData(trendResult.data || []);
          // Auto-expand the first zone so the heatmap is immediately visible
          if (loadedZones.length > 0) {
            setExpandedZone(loadedZones[0].zoneName);
          }
        }
      } catch (err) {
        console.error('Failed to load quest detail:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [api, questSetup, days]);

  if (loading) {
    return <div className="quest-detail-loading"><div className="loading-spinner small"></div> Loading zones...</div>;
  }

  // Build CL trend chart data — pivot contexts into columns
  const clTrendData = buildClTrendData(trendData);

  return (
    <div className="quest-detail">
      {/* CL Trend Chart */}
      {clTrendData.chartData.length > 0 && (
        <div className="quest-trend-section">
          <h4>FPS Trend by Zone (per CL)</h4>
          <div className="quest-trend-chart">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={clTrendData.chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="changelist" stroke="#888" fontSize={11} />
                <YAxis stroke="#888" fontSize={11} />
                <Tooltip contentStyle={{ backgroundColor: '#1e1e1e', border: '1px solid #444' }} />
                <Legend />
                <ReferenceLine y={45} stroke="#4CAF50" strokeDasharray="5 5" label={{ value: '45fps', fill: '#4CAF50', fontSize: 10 }} />
                <ReferenceLine y={30} stroke="#FF9800" strokeDasharray="5 5" label={{ value: '30fps', fill: '#FF9800', fontSize: 10 }} />
                {clTrendData.contexts.map((ctx, i) => (
                  <Line
                    key={ctx}
                    type="monotone"
                    dataKey={ctx}
                    stroke={CHART_COLORS[i % CHART_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    name={ctx}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Zone List */}
      <div className="quest-zones">
        <h4>Zones ({zones.length})</h4>
        {zones.map(zone => (
          <div key={zone.zoneName} className="quest-zone-item">
            <div
              className="quest-zone-header"
              onClick={() => setExpandedZone(prev => prev === zone.zoneName ? null : zone.zoneName)}
            >
              <span className="zone-expand-icon">{expandedZone === zone.zoneName ? '\u25BC' : '\u25B6'}</span>
              <span className={`zone-status ${getPerformanceStatus(zone.avgFps).className}`}>
                {getPerformanceStatus(zone.avgFps).emoji}
              </span>
              <span className="zone-name">{zone.zoneName}</span>
              <span className="zone-metrics">
                <span className="zone-fps">{zone.avgFps} avg fps</span>
                <span className="zone-fps-min">{zone.minFps} min</span>
                <span className="zone-gpu">{zone.avgGpuTime}ms gpu</span>
                <span className="zone-memory">{zone.avgMemory}MB mem</span>
                <span className="zone-points">{zone.pointCount} pts</span>
              </span>
            </div>

            {expandedZone === zone.zoneName && (
              <ZoneHeatmapDetail
                api={api}
                questSetup={questSetup}
                zoneName={zone.zoneName}
                days={days}
              />
            )}
          </div>
        ))}
        {zones.length === 0 && (
          <p className="quest-no-zones">No zone data found for this quest.</p>
        )}
      </div>
    </div>
  );
}

/**
 * Zone heatmap detail — thin wrapper around the shared ZoneHeatmapDisplay.
 * Just provides the quest-specific fetch function.
 */
function ZoneHeatmapDetail({ api, questSetup, zoneName, days }) {
  const fetchHeatmapData = useCallback(
    (runLimit) => api.getQuestPerformanceHeatmap(questSetup, zoneName, days, { runLimit }),
    [api, questSetup, zoneName, days]
  );
  const fetchMapConfig = useCallback(
    () => api.getHeatmapMapConfig(zoneName),
    [api, zoneName]
  );

  return (
    <ZoneHeatmapDisplay
      fetchHeatmapData={fetchHeatmapData}
      fetchMapConfig={fetchMapConfig}
      api={api}
      zoneName={zoneName}
    />
  );
}

/**
 * Pivot trend data (rows with changelist + context + avgFps) into chart-friendly format.
 * Returns { chartData: [{changelist, context1: fps, context2: fps, ...}], contexts: [] }
 */
function buildClTrendData(trendRows) {
  const contexts = [...new Set(trendRows.map(r => r.context))];
  const clMap = new Map();

  for (const row of trendRows) {
    if (!clMap.has(row.changelist)) {
      clMap.set(row.changelist, { changelist: row.changelist });
    }
    clMap.get(row.changelist)[row.context] = row.avgFps;
  }

  const chartData = [...clMap.values()].sort((a, b) => parseInt(a.changelist) - parseInt(b.changelist));
  return { chartData, contexts };
}

export default QuestPerformance;
